import React, { createContext, useContext, useState, useEffect, lazy as _reactLazy, Suspense } from 'react';
import { PayrollCountryGate } from './components/payroll/PayrollCountryGate';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// iter1114b - Retry-then-reload wrapper for React.lazy() to survive stale
// cached bundles. When the app is redeployed, a user with a cached
// index.html will still reference the old chunk hashes; requesting them
// returns 404 and React throws `Loading chunk XXX failed`, which then
// bubbles into the AppErrorBoundary and blocks the whole app.
//
// This wrapper catches that specific class of error and forces exactly one
// hard reload (session-scoped to avoid infinite loops). The reload fetches
// the fresh index.html which references the new chunk hashes.
const _AKF_CHUNK_RELOAD_KEY = 'akf_chunk_reload_at';
const lazy = (importer) => _reactLazy(() =>
  importer().catch((err) => {
    try {
      const msg = String(err && (err.message || err));
      const isChunkFail = /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported|Importing a module script failed/i.test(msg);
      if (isChunkFail && typeof window !== 'undefined') {
        const now = Date.now();
        const last = parseInt(window.sessionStorage.getItem(_AKF_CHUNK_RELOAD_KEY) || '0', 10);
        // Only reload once per 15s window - protects against infinite reload
        // if the underlying chunk really is missing on the CDN.
        if (!last || now - last > 15000) {
          window.sessionStorage.setItem(_AKF_CHUNK_RELOAD_KEY, String(now));
          window.location.reload();
          // Return a never-resolving promise so React doesn't render the error
          // fallback before the reload completes.
          return new Promise(() => {});
        }
      }
    } catch (_swallow) { /* fall through to rethrow */ }
    throw err;
  })
);

// iter1114b - Belt-and-braces: catch chunk-load failures at the global
// unhandledrejection level too. Some dynamic imports (route preloads,
// prefetches) resolve outside a React tree and won't hit the boundary.
if (typeof window !== 'undefined' && !window.__akf_chunk_retry_installed) {
  window.__akf_chunk_retry_installed = true;
  window.addEventListener('unhandledrejection', (evt) => {
    try {
      const reason = evt && evt.reason;
      const msg = String((reason && (reason.message || reason)) || '');
      if (/Loading chunk|ChunkLoadError|Failed to fetch dynamically imported|Importing a module script failed/i.test(msg)) {
        const key = 'akf_chunk_reload_at';
        const now = Date.now();
        const last = parseInt(window.sessionStorage.getItem(key) || '0', 10);
        if (!last || now - last > 15000) {
          window.sessionStorage.setItem(key, String(now));
          window.location.reload();
        }
      }
    } catch (_e) { /* noop */ }
  });
}
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import apiClient, { authApi, getCsrfToken, storeCsrfToken } from './utils/apiClient';
import ScrollToTop from './components/ScrollToTop';
import GlobalBackButton from './components/shared/GlobalBackButton';
import NotificationRouter from './components/NotificationRouter';
import NavigationWatchdog from './components/NavigationWatchdog';
import PushOptInPrompt from './components/notifications/PushOptInPrompt';
import LegalFormConfirmGate from './components/legal-form/LegalFormConfirmGate';
import TaxSetupGate from './components/tax-setup/TaxSetupGate';
import { LegalFormRequired } from './components/legal-form/LegalFormRequired';
import { consumeRedirect, authUrlWithReturnTo } from './utils/postLoginRedirect';
import AppShellSkeleton from './components/layout/AppShellSkeleton';
import DashboardLayout from './components/layout/DashboardLayout';
import SmartSuspenseFallback from './components/layout/SmartSuspenseFallback';
import BackgroundLockHost from './components/auth/BackgroundLockHost';
import { StepUpProvider } from './components/auth/StepUpProvider';
import { PageSkeleton } from './components/ui/LoadingSkeleton';
import { prefetchCoreRoutes } from './prefetchRoutes';
import { clearInstantCache } from './lib/instantCache';
import { startAutoFitNumbers } from './lib/autoFitNumbers';
import { BackgroundJobsProvider } from './contexts/BackgroundJobsContext';

// Pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const CameraTestPage = lazy(() => import('./pages/CameraTestPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const CreateInvoicePage = lazy(() => import('./pages/CreateInvoicePage'));
const EditInvoicePage = lazy(() => import('./pages/EditInvoicePage'));
const InvoiceDetailPage = lazy(() => import('./pages/InvoiceDetailPage'));
const BillsPage = lazy(() => import('./pages/BillsPage'));
const CreateBillPage = lazy(() => import('./pages/CreateBillPage'));
const EditBillPage = lazy(() => import('./pages/EditBillPage'));
const BillDetailPage = lazy(() => import('./pages/BillDetailPage'));
const OtherDocEditPage = lazy(() => import('./pages/OtherDocEditPage'));
const OtherDocDetailPage = lazy(() => import('./pages/OtherDocDetailPage'));
const EditByQueryRedirect = lazy(() => import('./pages/EditByQueryRedirect'));
const MyBanksPage = lazy(() => import('./pages/MyBanksPage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
// ChatPage removed - all chat goes through AkountFlyAIPage
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const CloseAccountWizard = lazy(() => import('./pages/CloseAccountWizard'));
const RecognitionPolicySettingsPage = lazy(() => import('./pages/RecognitionPolicySettingsPage'));  // iter 817 Phase 7
// iter928 - Stripe Connect onboarding return/refresh landings
const StripeConnectReturnPage = lazy(() => import('./pages/StripeConnectReturnPage'));
const StripeConnectRefreshPage = lazy(() => import('./pages/StripeConnectRefreshPage'));
const LearnPage = lazy(() => import('./pages/LearnPage'));
const ActivityRecoveryPage = lazy(() => import('./pages/ActivityRecoveryPage'));
const AdvancedViewPage = lazy(() => import('./pages/AdvancedViewPage'));
const ProductsServicesPage = lazy(() => import('./pages/ProductsServicesPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const TransactionReviewPage = lazy(() => import('./pages/TransactionReviewPage'));
// ReconciliationPage removed - reconciliation handled in BankFeedCenter
const BankFeedCenterPage = lazy(() => import('./pages/BankFeedCenterPage'));
const ActivityPage = lazy(() => import('./pages/ActivityPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AppWelcomePage = lazy(() => import('./pages/AppWelcomePage'));
const SetupCompletePage = lazy(() => import('./pages/SetupCompletePage'));
import InstallAppPrompt from './components/shared/InstallAppPrompt';
import { isStandalone } from './utils/pwa';
import { isIOSNative, waitForNative } from './utils/nativeBridge';
import IOSAccountPage, { IOSSetupPendingPage } from './pages/IOSAccountPage';

// iOS App Store build: tag every request so the backend applies iOS-safe
// behaviour (backlog ingestion always takes the free path, etc.).
waitForNative().then(() => {
  if (isIOSNative()) {
    axios.defaults.headers.common['X-Client-Platform'] = 'ios';
  }
}).catch(() => {});
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const DeferredRevenuePage = lazy(() => import('./pages/DeferredRevenuePage'));
const FixedAssetsPage = lazy(() => import('./pages/FixedAssetsPage'));
const PrepaidExpensesPage = lazy(() => import('./pages/PrepaidExpensesPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const GeneralLedgerReportPage = lazy(() => import('./pages/GeneralLedgerReportPage'));
const AkountFlyAIPage = lazy(() => import('./pages/AkountFlyAIPage'));
// iter971k-T · Help Center pages
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const HelpCategoryPage = lazy(() => import('./pages/HelpCategoryPage'));
const HelpArticlePage = lazy(() => import('./pages/HelpArticlePage'));
const TaxCompliancePage = lazy(() => import('./pages/TaxCompliancePage'));
const AnnualTaxComputationPage = lazy(() => import('./pages/AnnualTaxComputationPage'));
const MileagePage = lazy(() => import('./pages/MileagePage'));
const TaxPackAdminPage = lazy(() => import('./pages/TaxPackAdminPage'));
const TaxRemittancePage = lazy(() => import('./pages/TaxRemittancePage'));
const SettlementSubLedgersPage = lazy(() => import('./pages/SettlementSubLedgersPage'));
const PayrollHomePage = lazy(() => import('./pages/PayrollHomePage'));
const EmployerPayrollSettingsPage = lazy(() => import('./pages/EmployerPayrollSettingsPage'));
const PayrollSetupPage = lazy(() => import('./pages/PayrollSetupPage'));
const TaxSetupPage = lazy(() => import('./pages/TaxSetupPage'));
const PayrollPeoplePage = lazy(() => import('./pages/PayrollPeoplePage'));  // iter832: v2 engine settings
const PayrollRunsPage = lazy(() => import('./pages/PayrollRunsPage'));  // iter842: Sprint B.2 Pay Run UI
const PayrollYearEndPage = lazy(() => import('./pages/PayrollYearEndPage'));
const PayrollNoticesPage = lazy(() => import('./pages/PayrollNoticesPage'));
const PayrollReportsPage = lazy(() => import('./pages/PayrollReportsPage'));
const EmployeePortalPage = lazy(() => import('./pages/EmployeePortalPage'));  // iter859: Sprint 7 Self-Serve Portal
const DataMigrationPage = lazy(() => import('./pages/DataMigrationPage'));
const OpeningBalanceAIWizardPage = lazy(() => import('./pages/OpeningBalanceAIWizardPage'));  // iter 951. AI TB Import v3
const DataExportPage = lazy(() => import('./pages/DataExportPage'));
const OpeningBalancesSettingsPage = lazy(() => import('./pages/OpeningBalancesSettingsPage'));
const FXRatesPage = lazy(() => import('./pages/FXRatesPage'));
const BusinessTeamPage = lazy(() => import('./pages/business/BusinessTeamPage'));
const ScheduledReportsPage = lazy(() => import('./pages/business/ScheduledReportsPage'));
// HistoricalImportPage removed - redirects to DataMigrationPage
// Standalone accounting pages
const ChartOfAccountsPage = lazy(() => import('./pages/ChartOfAccountsPage'));
const GLBreakdownPage = lazy(() => import('./pages/GLBreakdownPage'));
const EquityLineBreakdownPage = lazy(() => import('./pages/EquityLineBreakdownPage'));
const ManualJournalsPage = lazy(() => import('./pages/ManualJournalsPage'));
const JournalDetailPage = lazy(() => import('./pages/JournalDetailPage'));
const ChannelReconciliationPage = lazy(() => import('./pages/ChannelReconciliationPage'));
const RecurringJournalsPage = lazy(() => import('./pages/RecurringJournalsPage'));
const SystemJournalsPage = lazy(() => import('./pages/SystemJournalsPage'));
const GeneralLedgerPage = lazy(() => import('./pages/GeneralLedgerPage'));
const AdvancedAccountingPage = lazy(() => import('./pages/AdvancedAccountingPage'));
const LoanSchedulePage = lazy(() => import('./pages/LoanSchedulePage'));
const LoanSchedulePageV2 = lazy(() => import('./pages/LoanSchedulePageV2'));
const InvestmentPage = lazy(() => import('./pages/InvestmentPage'));
const InvestmentPageV2 = lazy(() => import('./pages/InvestmentPageV2'));
const UntrackedSpecialItemsPage = lazy(() => import('./pages/UntrackedSpecialItemsPage'));
const BudgetPage = lazy(() => import('./pages/BudgetPage'));
const BudgetCreateV2Page = lazy(() => import('./pages/BudgetCreateV2Page'));
const PlaidOAuthResumePage = lazy(() => import('./pages/PlaidOAuthResumePage'));
const BudgetStatementsV2Page = lazy(() => import('./pages/BudgetStatementsV2Page'));
const BudgetVsActualsPage = lazy(() => import('./pages/BudgetVsActualsPage'));
const BudgetVsActuals3StatementPage = lazy(() => import('./pages/BudgetVsActuals3StatementPage'));
const PeriodClosePage = lazy(() => import('./pages/PeriodClosePage'));
const NotificationPreferencesPage = lazy(() => import('./pages/NotificationPreferencesPage'));
const SettlementDashboardPage = lazy(() => import('./pages/SettlementDashboardPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const BillingDashboardPage = lazy(() => import('./pages/BillingDashboardPage'));
const AdminBillingPage = lazy(() => import('./pages/AdminBillingPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const VerifyEmailPendingPage = lazy(() => import('./pages/VerifyEmailPendingPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
import CookieConsent from './components/CookieConsent';
import { ProactivePromptsProvider } from './components/ProactivePrompts';
import { RealtimeProvider } from './hooks/useRealtime';
import { UserModeProvider } from './contexts/UserModeContext';
import { AccountingModeProvider } from './contexts/AccountingModeContext';
import { DisplayModeProvider } from './contexts/DisplayModeContext';
import AppErrorBoundary from './components/common/AppErrorBoundary';
import { TourProvider } from './components/tour/TourProvider';
import AccountingStartDateSetup from './components/onboarding/AccountingStartDateSetup';
import { BillingProvider } from './contexts/BillingContext';
import { BillingAccessGate, BillingGateErrorBoundary } from './components/BillingAccessGate';

// Personal Finance Pages - DISABLED (under development, routes redirect to /dashboard)

// Admin Dashboard Pages
import AdminLayout from './components/layout/AdminLayout';
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminBusinessesPage = lazy(() => import('./pages/admin/AdminBusinessesPage'));
const AdminMessagingPage = lazy(() => import('./pages/admin/AdminMessagingPage'));
const AdminBroadcastsPage = lazy(() => import('./pages/admin/AdminBroadcastsPage'));  // iter 960 P2: product-update broadcasts
const AdminBankFeedHealthPage = lazy(() => import('./pages/admin/AdminBankFeedHealthPage'));
const AdminParserRulesPage = lazy(() => import('./pages/admin/AdminParserRulesPage'));  // Phase E · self-healing parser rules  // iter 963. Bank Feed Health card
const AdminReconIntegrityPage = lazy(() => import('./pages/admin/AdminReconIntegrityPage'));  // iter1450
const AdminActivityPage = lazy(() => import('./pages/admin/AdminActivityPage'));
const AdminSystemPage = lazy(() => import('./pages/admin/AdminSystemPage'));
const AdminFinancialOpsPage = lazy(() => import('./pages/admin/AdminFinancialOpsPage'));
const AdminAIAnalyticsPage = lazy(() => import('./pages/admin/AdminAIAnalyticsPage'));
const AdminIntegrationHealthPage = lazy(() => import('./pages/admin/AdminIntegrationHealthHubPage'));
const AdminRevenueReadinessPage = lazy(() => import('./pages/admin/AdminRevenueReadinessPage'));
const AdminDataQualityPage = lazy(() => import('./pages/admin/AdminDataQualityPage'));
const AdminSupportToolsPage = lazy(() => import('./pages/admin/AdminSupportToolsPage'));
const AdminCountryRequestsPage = lazy(() => import('./pages/admin/AdminCountryRequestsPage'));
const AdminPartnersPage = lazy(() => import('./pages/admin/AdminPartnersPage'));  // Partner & Channel Program
const AdminPartnerDetailPage = lazy(() => import('./pages/admin/AdminPartnerDetailPage'));
const AdminPartnerSettingsPage = lazy(() => import('./pages/admin/AdminPartnerSettingsPage'));
const PartnerPortalPage = lazy(() => import('./pages/PartnerPortalPage'));
const PartnerApplyPage = lazy(() => import('./pages/PartnerApplyPage'));
const ReferralLandingPage = lazy(() => import('./pages/ReferralLandingPage'));
const PartnerProgramPage = lazy(() => import('./pages/PartnerProgramPage'));
const PartnerPolicyPage = lazy(() => import('./pages/PartnerPolicyPage'));
const AdminRuleWatcherPage = lazy(() => import('./pages/admin/AdminRuleWatcherPage'));
const AdminMasterTaxPage = lazy(() => import('./pages/admin/AdminMasterTaxPage'));
// iter931 - Onboarding funnel analytics for admin
const AdminOnboardingFunnelPage = lazy(() => import('./pages/admin/AdminOnboardingFunnelPage'));
const AdminBankFeedClassifierPage = lazy(() => import('./pages/admin/AdminBankFeedClassifierPage'));
const AdminKnowledgeScreenshotsPage = lazy(() => import('./pages/admin/AdminKnowledgeScreenshotsPage')); // iter971k-V · KB screenshots
const AdminMerchantKBPage = lazy(() => import('./pages/admin/AdminMerchantKBPage')); // iter1349 · Merchant KB curation
const AdminEscalationsPage = lazy(() => import('./pages/admin/AdminEscalationsPage')); // iter971k-Y · Phase 4
const AdminRevenueHubPage = lazy(() => import('./pages/admin/AdminRevenueHubPage')); // Admin consolidation. Revenue & Billing hub
const AdminBankFeedsHubPage = lazy(() => import('./pages/admin/AdminBankFeedsHubPage'));
const AdminCommsHubPage = lazy(() => import('./pages/admin/AdminCommsHubPage'));
const AdminPlatformHealthHubPage = lazy(() => import('./pages/admin/AdminPlatformHealthHubPage'));
const AdminBusiness360Page = lazy(() => import('./pages/admin/AdminBusiness360Page')); // Business 360° drill-down

// REMOVED: ModeSelectionPage and EntityContext - SEPARATE PRODUCTS ARCHITECTURE
// Business and Personal are now separate products with separate logins

// Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

import { API_BASE as _APP_API_BASE, BACKEND_BASE } from './utils/apiUrl';
import ReportIssueHost from './components/report/ReportIssueHost';
import { installConsoleErrorRecorder } from './utils/diagnosticsBuffer';
const BACKEND_URL = BACKEND_BASE;
export const API = _APP_API_BASE;

// /login → /auth redirect that PRESERVES the query string (e.g. team
// invitation deep links carry ?redirect=/accept-invitation?token=...).
const LoginRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/auth${location.search}`} replace />;
};

// Configure axios defaults for backward compatibility
// New code should use apiClient from utils/apiClient.js

// Configure axios defaults so EVERY call site (including the ~50 raw-axios
// callers across the app) sends httpOnly auth cookies. Phase 3 (Feb 2026):
// the SOLE auth carrier is the httpOnly access_token cookie - no Bearer
// header, no localStorage token. CSRF double-submit is still required on
// mutating requests because cookies alone aren't CSRF-safe.
axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
  // Ensure withCredentials is set even if the caller created a child axios
  // instance without it. Safe to set on every request.
  config.withCredentials = true;

  // Add CSRF token for mutation requests (double-submit cookie pattern).
  const mutationMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
  if (mutationMethods.includes(config.method?.toUpperCase())) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  return config;
});

// Refresh token queue: prevents multiple simultaneous refresh calls
// when many 401s arrive at once (e.g., Settings page fires 9 API calls)
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshComplete = (token) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

const onRefreshFailed = (err) => {
  refreshSubscribers.forEach(cb => cb(null, err));
  refreshSubscribers = [];
};

const waitForRefresh = () => {
  return new Promise((resolve, reject) => {
    refreshSubscribers.push((token, err) => {
      if (err) reject(err);
      else resolve(token);
    });
  });
};

axios.interceptors.response.use(
  (response) => {
    // Store CSRF token if present
    if (response.data?.csrf_token) {
      storeCsrfToken(response.data.csrf_token);
    }
    // Auto-invalidate the Open-Tasks bar after any successful mutation that
    // could complete a task. Uses URL-pattern allow-list so we don't spam
    // the bar API on unrelated saves (settings, telemetry, etc.). Fires the
    // same `open-tasks-dirty` event that OpenTasksButton listens for -
    // gives users the "task disappears the moment it's done" behaviour.
    try {
      const method = (response.config?.method || '').toUpperCase();
      if (method && method !== 'GET' && method !== 'HEAD' && response.status < 400) {
        const url = response.config?.url || '';
        const TASK_MUTATION_PATTERNS = [
          '/bank-feeds/approve',
          '/bank-feeds/reclassify',
          '/bank-feeds/duplicates',
          '/bank-feeds/failed',
          '/bank-feeds/potential-duplicates',
          '/bank-feeds/link-to-existing',
          '/bank-feeds/process-manual',
          '/bank-feeds/upload-jobs',
          '/bank-transfer',
          '/reconciliation',
          '/invoices',
          '/bills',
          '/products-services',
          '/contacts',
          '/loans',
          '/investments',
          '/fixed-assets',
          '/prepayments',
          '/deferred-revenue',
          '/opening-balances',
          '/journals',
          '/journal-entries',
          '/period-close',
          '/tax-management',
          '/notifications/snooze',
          '/notifications/dismiss',
          '/setup-tasks',
        ];
        if (TASK_MUTATION_PATTERNS.some(p => url.includes(p))) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('open-tasks-dirty', { detail: { reason: url } }));
          }
        }
      }
    } catch { /* never let a UX side-effect break a successful request */ }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Phase A · compulsory tax setup: backend refuses intake/document creation until it is complete.
    if (error.response?.status === 409 && error.response?.data?.detail?.code === 'tax_setup_required') {
      toast.error('Complete your tax setup first — tax tracking is on and every transaction needs the full tax context.', { id: 'tax-setup-required' });
      window.dispatchEvent(new CustomEvent('akf-tax-setup-required', { detail: error.response.data.detail }));
    }

    // Handle billing access 403 responses from middleware
    if (error.response?.status === 403 && error.response?.data?.billing_status) {
      const { action_required, detail } = error.response.data;
      if (action_required === 'reactivate') {
        toast.error('Account locked: redirecting to billing', { id: 'billing-lock' });
        setTimeout(() => { window.location.href = '/billing'; }, 1500);
      } else if (action_required === 'pay_outstanding') {
        toast.error(detail || 'Account restricted: write operations disabled', { id: 'billing-restrict' });
      }
      return Promise.reject(error);
    }

    // Handle rate limiting - suppress retries and show a non-intrusive message.
    // Structured AI-pause 429s (chat_paused / scan_paused) are rendered by the
    // page itself - skip the generic toast for those.
    if (error.response?.status === 429) {
      const pauseCode = error.response?.data?.detail?.code;
      if (pauseCode !== 'chat_paused' && pauseCode !== 'scan_paused') {
        toast.error('Too many requests. Please wait a moment and try again.', { id: 'rate-limit', duration: 5000 });
      }
      return Promise.reject(error);
    }

    // Handle server errors (503/500) - show user-friendly message instead of cryptic error
    if (error.response?.status === 503 || error.response?.status === 500) {
      const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
      if (isAuthEndpoint) {
        toast.error('Service temporarily unavailable. Please try again in a moment.', { id: 'server-error', duration: 5000 });
      }
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      // iter930 - Honour `__silentAuth: true` flag on the request config:
      // optional onboarding/registration API calls (mono/config, plaid/config,
      // payments/providers/{country}, etc.) should NOT bounce the user back to
      // /auth via the global redirect - they're just informational. Caller
      // gets the rejection and can `.catch()` it locally.
      if (originalRequest?.__silentAuth) {
        return Promise.reject(error);
      }

      // Don't try to refresh when the failing call IS the refresh/login flow
      // itself - those endpoints can't be recovered by another refresh.
      // `/auth/me` IS retried though, because a stale access cookie (with a
      // valid refresh cookie) is exactly the scenario refresh is designed
      // for - refusing to retry /me here means a user with an expired
      // access cookie bounces to /auth on every reload.
      const url = originalRequest?.url || '';
      const NON_RETRYABLE = [
        '/auth/login',
        '/auth/register',
        '/auth/refresh',
        '/auth/logout',
        '/auth/forgot-password',
        '/auth/reset-password',
      ];
      if (NON_RETRYABLE.some((p) => url.includes(p))) {
        return Promise.reject(error);
      }

      // iter1236 - Non-idempotent uploads MUST NOT auto-replay after a 401.
      // These endpoints create a new server-side resource per call (a fresh
      // upload job with a new job_id and a re-uploaded file body). Silently
      // re-firing the multipart POST after a token refresh caused users to
      // see multiple duplicate parse-and-backlog cycles for what was a
      // single Upload click. We still refresh the token in the background
      // so subsequent GETs recover, but the upload itself is surfaced as
      // an error so the caller can prompt the user to retry deliberately.
      const NON_REPLAYABLE_UPLOAD = [
        '/bank-feeds/upload-background',
      ];
      if (NON_REPLAYABLE_UPLOAD.some((p) => url.includes(p))) {
        // Fire-and-forget token refresh so the next user action succeeds.
        try {
          if (!isRefreshing) {
            isRefreshing = true;
            axios.post(`${API}/auth/refresh`, {}, {
              headers: {
                'Content-Type': 'application/json',
                ...(getCsrfToken() ? { 'X-CSRF-Token': getCsrfToken() } : {}),
              },
              _retry: true,
            }).then((r) => {
              if (r.data?.csrf_token) storeCsrfToken(r.data.csrf_token);
              onRefreshComplete(true);
            }).catch((e) => {
              onRefreshFailed(e);
            }).finally(() => { isRefreshing = false; });
          }
        } catch (_) { /* swallow - the important part is not replaying the upload */ }
        // Surface a distinct error so callers can show a friendly retry hint.
        error.__uploadRetryBlocked = true;
        return Promise.reject(error);
      }
      
      originalRequest._retry = true;

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        try {
          await waitForRefresh();
          return axios(originalRequest);
        } catch (refreshErr) {
          return Promise.reject(refreshErr);
        }
      }

      // First 401 to arrive - initiate the refresh.
      // Phase 3: cookie-only refresh - httpOnly refresh_token cookie is
      // sent automatically; CSRF header guards against forged refreshes.
      isRefreshing = true;

      try {
        // Cross-tab lock (navigator.locks is browser-wide per origin):
        // serializes refreshes across windows so sibling tabs never race
        // the single-use rotation. Backend also has a 60s grace window.
        const doRefresh = () => axios.post(`${API}/auth/refresh`, {}, {
          headers: {
            'Content-Type': 'application/json',
            ...(getCsrfToken() ? { 'X-CSRF-Token': getCsrfToken() } : {}),
          },
          _retry: true, // prevent this request from triggering another refresh
        });
        const refreshResponse = (navigator.locks && navigator.locks.request)
          ? await navigator.locks.request('akountfly-token-refresh', doRefresh)
          : await doRefresh();

        if (refreshResponse.data?.csrf_token) {
          storeCsrfToken(refreshResponse.data.csrf_token);
        }

        isRefreshing = false;
        onRefreshComplete(true);

        // Retry original request - new access cookie is already set.
        return axios(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshFailed(refreshError);

        // Refresh failed. Only bounce to /auth from PROTECTED routes.
        // Public routes (landing, pricing, policy pages, magic-link
        // verification, etc.) must stay where they are - they don't need
        // a session, and silently hard-redirecting a guest off the
        // landing page is the same class of bug as the iter 702 auth-
        // loop. The redirect + localStorage wipe used to fire whenever
        // the path didn't literally start with /auth, so loading "/"
        // with any stale CSRF cookie value triggered: /auth/me 401 ->
        // refresh 401 -> wipe LS -> hard navigation to /auth.
        const PUBLIC_PATHS = [
          '/', '/auth', '/login',
          '/accept-invitation', '/verify-email', '/reset-password',
          '/pricing', '/privacy', '/privacy-policy', '/terms',
          '/terms-of-service', '/cookie-policy',
          // iter1343 - Help Center + article/category subpaths are public
          // (Apple Support URL, in-app help browsing without account). The
          // routes are declared as unauthenticated in the Router, but the
          // axios refresh-failure fallback used to hard-redirect them to
          // /auth on any stale-session 401, defeating the "public" contract.
          '/help',
          // Employee self-service portal uses its own magic-link token auth (no business session);
          // a stale business cookie must never bounce an employee off their payslips.
          '/portal',
          // Partner & referral program public routes (iter: Partner Program)
          '/partner', '/partners', '/r',
        ];
        const pathname = window.location.pathname;
        const isPublic = PUBLIC_PATHS.some((p) =>
          p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(p + '/')
        );
        if (!isPublic) {
          localStorage.removeItem('akountfly_user');
          localStorage.removeItem('akountfly_business');
          localStorage.removeItem('akountfly_business_cached_for');
          localStorage.removeItem('akountfly_csrf');
          window.location.href = authUrlWithReturnTo();
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);

  // Native wrapper push: register the device for this user after login and on
  // every app open (re-runs whenever the signed-in user changes).
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    import('./utils/nativeBridge').then(({ registerPushForUser }) => {
      if (!cancelled) registerPushForUser(user.id);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  const [loading, setLoading] = useState(true);
  // True once the server has answered the business lookup for this session
  // (or there is no session). Until then ProtectedRoute must not conclude
  // "no business -> onboarding" from a partially populated cache.
  const [businessResolved, setBusinessResolved] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      // Check for saved user data (from localStorage for backward compatibility)
      const savedUser = localStorage.getItem('akountfly_user');
      const savedBusiness = localStorage.getItem('akountfly_business');
      // Session existence is signalled by the readable CSRF cookie
      // (set alongside the httpOnly access_token cookie on login/refresh).
      // We can't read access_token itself, but if CSRF is present we know
      // we have an active cookie session worth verifying with /auth/me.
      //
      // iter 741 - TRUST THE COOKIE ONLY. localStorage user/business
      // can outlive the cookie (cookie expired, server-side logout from
      // another device, etc.). If only localStorage data is present
      // without a CSRF cookie, the visitor is effectively anonymous and
      // the cached data must be purged so we don't leak "you are
      // logged in" state onto public pages (Pricing, Landing).
      const hasSession = Boolean(getCsrfToken());

      if (!hasSession && !savedUser) {
        // Truly anonymous - no cookie signal AND no cached identity.
        setBusinessResolved(true);
        setLoading(false);
        return;
      }
      // NOTE: if the CSRF cookie expired but we have a cached user, we do
      // NOT treat the visitor as anonymous - the httpOnly refresh cookie
      // may still be valid. We verify with /auth/me below (the axios
      // interceptor transparently refreshes on 401), which eliminates the
      // "flash of the auth page" when opening the app in a new tab.
      
      // Use cached data immediately to avoid flash
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (e) {
          localStorage.removeItem('akountfly_user');
        }
      }
      if (savedBusiness) {
        try {
          setBusiness(JSON.parse(savedBusiness));
        } catch (e) {
          localStorage.removeItem('akountfly_business');
        }
      }

      // Optimistic boot: with a cached identity we render the app IMMEDIATELY
      // and verify the session in the background. If verification 401s the
      // state is cleared below and ProtectedRoute redirects to /auth.
      if (savedUser) setLoading(false);
      
      // Verify session with server (uses cookie or token)
      try {
        const userRes = await axios.get(`${API}/auth/me`);
        setUser(userRes.data);
        localStorage.setItem('akountfly_user', JSON.stringify(userRes.data));
        
        // Refresh business data
        try {
          const businessRes = await axios.get(`${API}/business`);
          setBusiness(businessRes.data);
          localStorage.setItem('akountfly_business', JSON.stringify(businessRes.data));
          localStorage.setItem('akountfly_business_cached_for', userRes.data?.id || '');
        } catch (bizError) {
          // Business not setup yet - that's ok
          if (bizError.response?.status !== 404) {
            console.error('Error fetching business:', bizError);
          }
        }
      } catch (error) {
        // If 401, user is not authenticated - don't redirect from here
        if (error.response?.status === 401) {
          // Clear stale data so subsequent renders are truly anonymous.
          setUser(null);
          setBusiness(null);
          localStorage.removeItem('akountfly_user');
          localStorage.removeItem('akountfly_business');
          localStorage.removeItem('akountfly_business_cached_for');
        }
      }
      setBusinessResolved(true);
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password, otpCode = null, accountType = 'business', options = {}) => {
    const result = await authApi.login(email, password, otpCode, accountType);
    
    // If 2FA is required, return without storing
    if (result.requires_2fa) {
      return { 
        requires_2fa: true, 
        two_fa_methods: result.two_fa_methods, 
        user: result.user,
        account_type: result.account_type || accountType
      };
    }
    
    // Store user data (now includes account_type). React state for `user`
    // is set only AFTER the business fetch below: PublicEntryRoute redirects
    // the instant `user` exists, and ProtectedRoute would bounce a user
    // with no business in state to /onboarding.
    localStorage.setItem('akountfly_user', JSON.stringify(result.user));
    try { sessionStorage.removeItem('akf_skip_welcome_back'); } catch { /* noop */ }
    // Mark unlocked - fresh password login resets the background-lock clock.
    try {
      const { markUnlockedNow, markActiveNow } = await import('./utils/webauthn');
      markUnlockedNow(); markActiveNow();
    } catch { /* noop */ }

    // Fetch business entity. Retry once on failure - some browsers race the
    // cookie-jar commit against the very next request after Set-Cookie, so
    // the first /business call can 401 immediately after a successful
    // /auth/login. A second attempt always succeeds because the cookies
    // are guaranteed committed by then. Without this retry, a transient
    // 401 here causes ProtectedRoute to bounce the user to /onboarding
    // step 1 even though they have an existing business.
    let businessData = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const businessRes = await apiClient.get('/business');
        businessData = businessRes.data;
        break;
      } catch (e) {
        const st = e?.response?.status;
        if (attempt < 2 && (st === 401 || st === 429)) {
          // 401: let the browser commit the auth cookies; 429: rate-limited - back off and retry
          await new Promise(r => setTimeout(r, st === 429 ? 1500 : 250));
          continue;
        }
        // 404 (no business yet) or final attempt failure - onboarding handles it
        break;
      }
    }
    if (businessData) {
      setBusiness(businessData);
      localStorage.setItem('akountfly_business', JSON.stringify(businessData));
      localStorage.setItem('akountfly_business_cached_for', result?.user?.id || '');
    }
    // Native app: mint the device token now (session cookies are live and
    // the auth page is still mounted) so the next sign-in can use Face ID /
    // Touch ID. Redeeming it always requires the OS biometric prompt.
    if (options.enrollNativeBiometric !== false) {
      try {
        const { isNativeApp, getBiometryInfo } = await import('./utils/nativeBridge');
        if (isNativeApp()) {
          const { hasDeviceTokenLocally, registerDeviceToken } = await import('./utils/deviceToken');
          const info = await getBiometryInfo();
          if (info.available && info.enrolled && !hasDeviceTokenLocally(email)) {
            await registerDeviceToken({ email, biometryType: info.type });
          }
        }
      } catch (e) { console.warn('native biometric enrol skipped:', e?.message || e); }
    }
    setBusinessResolved(true);
    setUser(result.user);
    try {
      const { registerNativePushToken } = await import('./utils/nativePush');
      registerNativePushToken(); // no-op unless the wrapper already has permission
    } catch { /* noop */ }
    
    return result;
  };

  const register = async (email, password, fullName, accountType = 'business', consentOptions = {}) => {
    const result = await authApi.register(email, password, fullName, accountType, consentOptions);
    
    // Store user data (now includes account_type)
    localStorage.setItem('akountfly_user', JSON.stringify(result.user));
    setUser(result.user);
    
    // Clear any stale business data from a previous session/user
    // so ProtectedRoute correctly redirects to onboarding
    localStorage.removeItem('akountfly_business');
    localStorage.removeItem('akountfly_business_cached_for');
    setBusiness(null);
    
    return result;
  };

  const logout = async () => {
    // An explicit sign-out usually means "another account next" - the next /auth visit shows the
    // plain form, not the previous user's Welcome-Back card (which pre-filled their e-mail and made
    // the next person's password fail against the wrong account). Set FIRST: interceptors may
    // hard-redirect to /auth before the rest of this function runs.
    try { sessionStorage.setItem('akf_skip_welcome_back', '1'); } catch { /* noop */ }
    // iter1102 - Revoke the device_token FIRST so future biometric
    // unlocks on this device stop working immediately. Fire-and-forget
    // (never blocks logout even if the network call fails).
    try {
      const email = user?.email;
      if (email) {
        try {
          const { unregisterNativePushToken } = await import('./utils/nativePush');
          unregisterNativePushToken();
        } catch { /* noop */ }
        const { revokeDeviceToken } = await import('./utils/deviceToken');
        // revokeDeviceToken clears local storage synchronously + POSTs
        // in the background.
        revokeDeviceToken(email).catch(() => { /* best-effort */ });
      }
    } catch { /* noop */ }
    try {
      await authApi.logout();
    } catch (_) { /* server-side logout failure must not trap the user */ }
    setUser(null);
    setBusiness(null);
    localStorage.removeItem('akountfly_user');
    localStorage.removeItem('akountfly_business');
    localStorage.removeItem('akountfly_business_cached_for');
    localStorage.removeItem('akountfly_csrf');
    // Clear reauth timestamps; biometric enrollment (device passkey)
    // deliberately stays so returning user can biometric-unlock again after
    // one password re-entry (matches Wise/Revolut behaviour).
    try {
      ['akf_biometric_last_unlock_at', 'akf_biometric_last_step_up_at', 'akf_last_active_at', 'akf_biometric_enroll_pending']
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* noop */ }
    clearInstantCache();
    // Clear the app-icon unread badge so a logged-out device isn't left
    // wearing another user's unread count.
    try {
      if (typeof navigator !== 'undefined' && 'clearAppBadge' in navigator) {
        navigator.clearAppBadge();
      }
    } catch { /* noop */ }
    // Wipe copilot chat state so the next user on this browser can't see it
    try {
      sessionStorage.removeItem('akf_copilot_ui_v1');
      localStorage.removeItem('akf_copilot_session_id_v1');
    } catch (_e) { /* noop */ }
    // Straight to the auth page - no landing-page detour.
    window.location.href = '/auth';
  };

  const setupBusiness = async (businessData) => {
    const response = await axios.post(`${API}/business/setup`, businessData);
    setBusiness(response.data);
    localStorage.setItem('akountfly_business', JSON.stringify(response.data));
    localStorage.setItem('akountfly_business_cached_for', user?.id || '');
    return response.data;
  };

  const refreshBusiness = async () => {
    try {
      const response = await axios.get(`${API}/business`);
      setBusiness(response.data);
      localStorage.setItem('akountfly_business', JSON.stringify(response.data));
      localStorage.setItem('akountfly_business_cached_for', user?.id || '');
    } catch (e) {
      setBusiness(null);
    }
  };

  // iter971k-C - Refresh the current user (e.g. after email verification
  // so ProtectedRoute + gates immediately see email_verified=true without
  // requiring a full page reload).
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      localStorage.setItem('akountfly_user', JSON.stringify(response.data));
      return response.data;
    } catch (e) {
      return null;
    }
  };

  // iter1086g - Apply the result of a WebAuthn / Face ID login. Mirrors
  // the tail of `login()` so the AuthContext state (user + business) is
  // fully hydrated BEFORE the caller navigates. Without this, biometric
  // sign-in wrote to localStorage but never called setUser/setBusiness
  // - ProtectedRoute would then bounce to /auth or /onboarding because
  // React state showed no user / no business, causing the "perpetual
  // flash" symptom and the wrong-page-on-reopen bug.
  const applyBiometricLoginResult = async (result) => {
    if (!result || !result.user) return null;
    localStorage.setItem('akountfly_user', JSON.stringify(result.user));
    try {
      const { markUnlockedNow, markActiveNow } = await import('./utils/webauthn');
      markUnlockedNow();
      markActiveNow();
    } catch { /* noop */ }
    // Same 250ms cookie-commit race retry as password login().
    let businessData = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const businessRes = await apiClient.get('/business');
        businessData = businessRes.data;
        break;
      } catch (e) {
        if (attempt === 0 && e?.response?.status === 401) {
          await new Promise(r => setTimeout(r, 250));
          continue;
        }
        break;
      }
    }
    if (businessData) {
      setBusiness(businessData);
      localStorage.setItem('akountfly_business', JSON.stringify(businessData));
      localStorage.setItem('akountfly_business_cached_for', result?.user?.id || '');
    }
    setBusinessResolved(true);
    setUser(result.user);
    return result;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      business, 
      loading, 
      businessResolved,
      login, 
      register, 
      logout, 
      setupBusiness,
      refreshBusiness,
      refreshUser,
      applyBiometricLoginResult,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// /signup preserves query params (crucially `?src=app` for the native
// handoff) while routing to the /auth register tab. `<Navigate>` alone
// drops the search string on React Router 6.
const SignupRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (!params.get('mode')) params.set('mode', 'register');
  return <Navigate to={`/auth?${params.toString()}`} replace />;
};

// Protected Route - SEPARATE PRODUCTS ARCHITECTURE
const ProtectedRoute = ({ children, requireBusiness = true }) => {
  const { user, business, loading, businessResolved } = useAuth();
  const location = useLocation();

  if (loading) {
    return <AppShellSkeleton />;
  }
  // Cached identity but no business yet (cache partially wiped, native
  // WebView storage, slow network): keep the shell up until the server
  // has answered instead of flashing "Account setup pending".
  if (user && requireBusiness && !business && !businessResolved
      && !localStorage.getItem('akountfly_business')) {
    return <AppShellSkeleton />;
  }

  if (!user) {
    const target = `${location.pathname}${location.search || ''}`;
    return <Navigate to={`/auth?redirect=${encodeURIComponent(target)}`} state={{ from: location }} replace />;
  }

  // iter971k-C - EMAIL VERIFICATION GATE (pre-onboarding only)
  // Rule: an unverified user with NO business must verify before proceeding.
  // Grandfathered - if they already have a business (successfully onboarded
  // before verification was enforced), we no longer block them. A gentle
  // in-app nudge lives elsewhere (banner).
  const isBusinessAccount = (user?.account_type || 'business') !== 'personal';
  const hasBusiness = !!business || !!localStorage.getItem('akountfly_business');
  if (isBusinessAccount && !user.email_verified && !hasBusiness) {
    // Exempt the verify-pending page itself + /settings (email change) +
    // the /verify-email confirmation page so the SPA can still land on
    // its own flows without a redirect loop.
    const exempt = (
      location.pathname === '/verify-email-pending'
      || location.pathname === '/verify-email'
      || location.pathname.startsWith('/settings')
    );
    if (!exempt) {
      return <Navigate to="/verify-email-pending" replace />;
    }
  }

  // SEPARATE PRODUCTS: Route based on account_type
  const accountType = user?.account_type || 'business';
  
  if (accountType === 'personal') {
    // Personal Finance is under development - redirect to business dashboard
    return <Navigate to="/dashboard" replace />;
  } else {
    // Business users need business setup (admin routes are exempt)
    // Check both React state AND localStorage (handles race condition after onboarding redirect)
    if (requireBusiness && !business && location.pathname !== '/onboarding' && !location.pathname.startsWith('/admin')) {
      const storedBusiness = localStorage.getItem('akountfly_business');
      if (!storedBusiness) {
        return <Navigate to="/onboarding" replace />;
      }
      // Validate stored business belongs to the current user (prevents stale data after re-registration).
      // Membership-aware: invited members cache a business whose user_id is the
      // OWNER's id, so compare against who the cache was fetched for, not the
      // business owner. Legacy caches without the companion key fall back to
      // the old owner-id comparison.
      try {
        const parsed = JSON.parse(storedBusiness);
        const cachedFor = localStorage.getItem('akountfly_business_cached_for');
        const staleCache = cachedFor
          ? (user?.id && cachedFor !== user.id)
          : (parsed.user_id && user?.id && parsed.user_id !== user.id);
        if (staleCache) {
          localStorage.removeItem('akountfly_business');
          localStorage.removeItem('akountfly_business_cached_for');
          return <Navigate to="/onboarding" replace />;
        }
      } catch {
        localStorage.removeItem('akountfly_business');
        localStorage.removeItem('akountfly_business_cached_for');
        return <Navigate to="/onboarding" replace />;
      }
    }
  }

  // Post-onboarding: no hard block. Users who registered before this
  // change and haven't verified yet keep full app access (grandfathered).
  // A dismissible in-app banner nudges them via the shell layout.

  return <BillingGateErrorBoundary><BillingAccessGate>{children}</BillingAccessGate></BillingGateErrorBoundary>;
};

// Sep 2026 - signed-in visitors opening "/" or "/auth" must land straight on
// their dashboard. Previously the Landing/Auth chunk rendered first, called
// /auth/me, then hard-reloaded to /dashboard (visible flash + double boot).
// With a cached identity we render the app-shell skeleton and navigate
// client-side; anonymous visitors see the public page exactly as before.
const publicEntryDestination = (user, search) => {
  if (user?.is_platform_admin) { consumeRedirect(); return '/admin'; }
  const redirect = new URLSearchParams(search).get('redirect');
  if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) { consumeRedirect(); return redirect; }
  const remembered = consumeRedirect();
  if (remembered) return remembered;
  if (user?.account_type === 'personal') return '/personal';
  return '/dashboard';
};

const PublicEntryRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  let hasCachedIdentity = false;
  try { hasCachedIdentity = Boolean(localStorage.getItem('akountfly_user')); } catch { /* storage blocked */ }
  if (user) {
    return <Navigate to={publicEntryDestination(user, location.search)} replace />;
  }
  if (loading && hasCachedIdentity) {
    return <AppShellSkeleton />;
  }
  return children;
};


// iter922d - /reconciliation/:accountId from older action-engine notifications.
// Bank reconciliation lives inside the Bank Transactions Center now; we forward the
// account id so the center can scope the feed to the user-clicked account.
const ReconciliationDeepLinkRedirect = () => {
  const { accountId } = useParams();
  const qs = accountId ? `?tab=reconciliation&bank_account_id=${encodeURIComponent(accountId)}` : '';
  return <Navigate to={`/bank-feeds${qs}`} replace />;
};

// Preserves ?bank_id= from legacy "/reconciliation?bank_id=X" links so they
// land on the exact account's reconciliation report.
const ReconciliationQueryRedirect = () => {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const bankId = params.get('bank_id') || params.get('bank_account_id');
  const qs = bankId ? `?tab=reconciliation&bank_account_id=${encodeURIComponent(bankId)}` : '?tab=reconciliation';
  return <Navigate to={`/bank-feeds${qs}`} replace />;
};

// Sep 2026 - legacy / renamed paths used by old emails, push notifications and
// bookmarks. Redirects to the canonical route while preserving the query string.
const LegacyRedirect = ({ to, mapQuery }) => {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  if (mapQuery) mapQuery(params);
  const qs = params.toString();
  const sep = to.includes('?') ? '&' : '?';
  return <Navigate to={`${to}${qs ? `${sep}${qs}` : ''}`} replace />;
};

const bankFeedSectionMap = (params) => {
  const section = params.get('section');
  if (section === 'review' || section === 'pending') {
    params.delete('section');
    params.set('tab', 'transactions');
    params.set('filter', 'pending');
  }
};

const LEGACY_ROUTES = [
  ['/bank-feed-center', '/bank-feeds', bankFeedSectionMap],
  ['/bank-feed-center/review', '/bank-feeds/review'],
  ['/bank-feed', '/bank-feeds', bankFeedSectionMap],
  ['/bank-feed/review', '/bank-feeds/review'],
  ['/banking/bank-feeds', '/bank-feeds'],
  ['/banking/upload-statement', '/bank-feeds'],
  ['/transaction-review', '/transactions/review'],
  ['/bank-accounts', '/banks'],
  ['/my-banks', '/banks'],
  ['/budgets', '/budget'],
  ['/prepayments', '/prepaid-expenses'],
  ['/modules/prepayments', '/prepaid-expenses'],
  ['/modules/loans', '/loans'],
  ['/modules/investments', '/investments'],
  ['/modules/fixed-assets', '/fixed-assets'],
  ['/modules/deferred-revenue', '/deferred-revenue'],
  ['/get-paid', '/invoices'],
  ['/pay-bills', '/bills'],
  ['/invoice', '/invoices'],
  ['/business-reports', '/reports'],
  ['/reports/pl', '/reports?report=pnl'],
  ['/reports/profit-loss', '/reports?report=pnl'],
  ['/reports/pnl', '/reports?report=pnl'],
  ['/reports/balance-sheet', '/reports?report=balance_sheet'],
  ['/reports/cash-flow', '/reports?report=cash_flow'],
  ['/reports/trial-balance', '/reports?report=trial_balance'],
  ['/settings/data-export', '/data-export'],
  ['/settings/general', '/settings'],
  ['/settings/equity', '/opening-balances'],
  ['/settings/historical-data', '/data-migration?tab=audit'],
  ['/wht-remittance', '/tax-remittance'],
];

// iter1300 - Mono (and future provider) browser-return forwarder.
const BankingReturnForward = () => {
  const loc = useLocation();
  return <Navigate to={`/bank-feeds${loc.search || ''}`} replace />;
};


// Sep 2026 · "URL changes but the page doesn't" — two-layer fix.
// 1) <BrowserRouter unstable_useTransitions={false}>: router state updates are
//    no longer wrapped in React.startTransition, so a navigation can never be
//    held back by (or entangled with) a suspended/pending transition; the
//    rendered route always changes in the same synchronous commit as the URL.
// 2) Re-keying this boundary per pathname mounts a fresh boundary on every
//    navigation, so the route skeleton shows at once while a lazy chunk loads
//    and the stale page is never mistaken for the destination.
// 3) <NavigationWatchdog/> hard-navigates if URL and rendered route still
//    disagree for >2.5s (last-resort self-heal, logged to diagnostics).
const RouteSuspense = ({ children }) => {
  const { pathname } = useLocation();
  return (
    <Suspense key={pathname} fallback={<SmartSuspenseFallback />}>
      {children}
    </Suspense>
  );
};

function App() {
  useEffect(() => {
    // Partner referral code: captured on ANY public URL (?ref=CODE) and kept until registration,
    // so a visitor can browse Pricing/Help/Partners before signing up and still be attributed.
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref && /^[A-Za-z0-9-]{3,40}$/.test(ref)) {
        localStorage.setItem('akf_ref', ref.toUpperCase());
        document.cookie = `akf_ref=${encodeURIComponent(ref.toUpperCase())}; path=/; max-age=${365 * 86400}; SameSite=Lax`;
      }
    } catch (_) { /* noop */ }
    prefetchCoreRoutes();
    installConsoleErrorRecorder();
    const stopAutoFit = startAutoFitNumbers();
    // Signal to index.html that React has committed - the splash fades out.
    try { document.documentElement.dataset.appReady = '1'; } catch (_) { /* noop */ }
    // Tell the native wrapper the web app has painted so it can drop its
    // own splash deterministically (cold start via universal link included).
    try {
      window.ReactNativeWebView?.postMessage?.(JSON.stringify({ type: 'web-ready', path: window.location.pathname }));
      window.NativeApp?.onWebReady?.(window.location.pathname);
    } catch (_) { /* noop */ }
    return stopAutoFit;
  }, []);
  return (
    <>
    <AppErrorBoundary>
    <HelmetProvider>
    <BrowserRouter unstable_useTransitions={false}>
      <NavigationWatchdog />
      <ScrollToTop />
      <GlobalBackButton />
      <NotificationRouter />
      <AuthProvider>
        <PushOptInPrompt />
        <LegalFormConfirmGate />
        <TaxSetupGate />
        <StepUpProvider>
          {/* BiometricEnrollHost removed — biometric enrollment now happens
              explicitly at login via the "Use Face ID next time" checkbox
              on AuthPage. Users who skip it can enable it from Settings >
              Security anytime. */}
          <BackgroundLockHost />
        <BillingProvider>
          <DisplayModeProvider>
          <TourProvider>
          <AccountingModeProvider>
            <UserModeProvider>
              <RealtimeProvider>
                <BackgroundJobsProvider>
                <ProactivePromptsProvider>
                  <Toaster position="top-right" richColors closeButton />
                <RouteSuspense>
        <Routes>
                  <Route path="/" element={<PublicEntryRoute>{isStandalone() ? <AppWelcomePage /> : <LandingPage />}</PublicEntryRoute>} />
                <Route path="/auth" element={<PublicEntryRoute><AuthPage /></PublicEntryRoute>} />
                {/* Mobile signup handoff: one-time code → cookie session (public, no guard) */}
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/camera-test" element={<CameraTestPage />} />
                <Route path="/login" element={<LoginRedirect />} />
                <Route path="/signup" element={<SignupRedirect />} />
                <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/verify-email-pending" element={
                  <ProtectedRoute requireBusiness={false}>
                    <VerifyEmailPendingPage />
                  </ProtectedRoute>
                } />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                  {isIOSNative() ? <IOSSetupPendingPage /> : <OnboardingPage />}
                </ProtectedRoute>
              } />
              <Route path="/setup-complete" element={
                <ProtectedRoute>
                  <SetupCompletePage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <DashboardLayout><NotificationsPage /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/invoices" element={
              <ProtectedRoute>
                <InvoicesPage />
              </ProtectedRoute>
            } />
            <Route path="/invoices/new" element={
              <ProtectedRoute>
                <CreateInvoicePage />
              </ProtectedRoute>
            } />
            <Route path="/invoices/create" element={
              <ProtectedRoute>
                <CreateInvoicePage />
              </ProtectedRoute>
            } />
            <Route path="/invoices/:id" element={
            <ProtectedRoute>
              <InvoiceDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/invoices/:id/edit" element={
            <ProtectedRoute>
              {/* iter971k-X · Edit=Create parity - reuse CreateInvoicePage in edit mode */}
              <CreateInvoicePage />
            </ProtectedRoute>
          } />
          <Route path="/bills" element={
            <ProtectedRoute>
              <BillsPage />
            </ProtectedRoute>
          } />
          <Route path="/create-bill" element={
            <ProtectedRoute>
              <CreateBillPage />
            </ProtectedRoute>
          } />
          <Route path="/bills/new" element={
            <ProtectedRoute>
              <CreateBillPage />
            </ProtectedRoute>
          } />
          <Route path="/bills/create" element={
            <ProtectedRoute>
              <CreateBillPage />
            </ProtectedRoute>
          } />
          <Route path="/bills/:id" element={
            <ProtectedRoute>
              <BillDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/bills/:id/edit" element={
            <ProtectedRoute>
              {/* iter971k-X · Edit=Create parity - reuse CreateBillPage in edit mode */}
              <CreateBillPage />
            </ProtectedRoute>
          } />
          <Route path="/banks" element={
            <ProtectedRoute>
              <MyBanksPage />
            </ProtectedRoute>
          } />
          <Route path="/other-payments/:id/edit" element={
            <ProtectedRoute>
              <DashboardLayout>
                <OtherDocEditPage docType="other_payment" />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/other-payments/:id" element={
            <ProtectedRoute>
              <DashboardLayout>
                <OtherDocDetailPage docType="other_payment" />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/other-receipts/:id/edit" element={
            <ProtectedRoute>
              <DashboardLayout>
                <OtherDocEditPage docType="other_receipt" />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/other-receipts/:id" element={
            <ProtectedRoute>
              <DashboardLayout>
                <OtherDocDetailPage docType="other_receipt" />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/insights" element={
            <ProtectedRoute>
              <InsightsPage />
            </ProtectedRoute>
          } />
          <Route path="/chat" element={
            <ProtectedRoute>
              <AkountFlyAIPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } />
          {/* Iter 936 - Unified Close Account / Cancel Subscription wizard */}
          <Route path="/account/close" element={
            <ProtectedRoute>
              <DashboardLayout><CloseAccountWizard /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/team" element={
            <ProtectedRoute>
              <DashboardLayout>
                <BusinessTeamPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings/opening-balances" element={
            <ProtectedRoute>
              <OpeningBalancesSettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/start-date" element={
            <ProtectedRoute>
              <AccountingStartDateSetup />
            </ProtectedRoute>
          } />
          <Route path="/settings/notifications" element={
            <ProtectedRoute>
              <NotificationPreferencesPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/recognition-policy" element={
            <ProtectedRoute>
              <RecognitionPolicySettingsPage />
            </ProtectedRoute>
          } />
          {/* iter928 - Stripe Connect onboarding return + refresh */}
          <Route path="/settings/stripe/return" element={
            <ProtectedRoute>
              <StripeConnectReturnPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/stripe/refresh" element={
            <ProtectedRoute>
              <StripeConnectRefreshPage />
            </ProtectedRoute>
          } />
          <Route path="/activity" element={
            <ProtectedRoute>
              <ActivityPage />
            </ProtectedRoute>
          } />
          <Route path="/advanced-view" element={<Navigate to="/advanced-view-hub" replace />} />
          <Route path="/advanced-view-hub" element={
            <ProtectedRoute>
              <AdvancedViewPage />
            </ProtectedRoute>
          } />
          <Route path="/settlement-dashboard" element={
            <ProtectedRoute>
              <SettlementDashboardPage />
            </ProtectedRoute>
          } />
          {/* Standalone Accounting Pages */}
          <Route path="/chart-of-accounts" element={
            <ProtectedRoute>
              <ChartOfAccountsPage />
            </ProtectedRoute>
          } />
          <Route path="/gl-breakdown/:glCode" element={
            <ProtectedRoute>
              <DashboardLayout>
                <GLBreakdownPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/equity-breakdown/:lineType" element={
            <ProtectedRoute>
              <DashboardLayout>
                <EquityLineBreakdownPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/manual-journals" element={
            <ProtectedRoute>
              <ManualJournalsPage />
            </ProtectedRoute>
          } />
          <Route path="/journals/:id" element={
            <ProtectedRoute>
              <JournalDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/bank-accounts/:id/channel-reconciliation" element={
            <ProtectedRoute>
              <ChannelReconciliationPage />
            </ProtectedRoute>
          } />
          <Route path="/recurring-journals" element={
            <ProtectedRoute>
              <RecurringJournalsPage />
            </ProtectedRoute>
          } />
          <Route path="/system-journals" element={
            <ProtectedRoute>
              <SystemJournalsPage />
            </ProtectedRoute>
          } />
          <Route path="/general-ledger" element={
            <ProtectedRoute>
              <GeneralLedgerPage />
            </ProtectedRoute>
          } />
          <Route path="/products-services" element={
            <ProtectedRoute>
              <ProductsServicesPage />
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/reports/general-ledger" element={
            <ProtectedRoute>
              <GeneralLedgerReportPage />
            </ProtectedRoute>
          } />
          <Route path="/transactions/review" element={
            <ProtectedRoute>
              <TransactionReviewPage />
            </ProtectedRoute>
          } />
          <Route path="/reconciliation" element={<ReconciliationQueryRedirect />} />
          <Route path="/bank-feeds/review" element={<Navigate to="/bank-feeds?tab=transactions&filter=pending" replace />} />
          {/* iter1300 - provider (Mono) return path. Deliberately NOT in the
              AASA universal-link claims so iOS never hijacks the browser
              return into the native app; forwards query params to /bank-feeds. */}
          <Route path="/banking-return" element={<BankingReturnForward />} />
          <Route path="/bank-feeds" element={
            <ProtectedRoute>
              <BankFeedCenterPage />
            </ProtectedRoute>
          } />
          <Route path="/deferred-revenue" element={
            <ProtectedRoute>
              <DeferredRevenuePage />
            </ProtectedRoute>
          } />
          <Route path="/prepaid-expenses" element={
            <ProtectedRoute>
              <PrepaidExpensesPage />
            </ProtectedRoute>
          } />
          <Route path="/fixed-assets" element={
            <ProtectedRoute>
              <FixedAssetsPage />
            </ProtectedRoute>
          } />
          <Route path="/fixed-assets/:id/edit" element={
            <ProtectedRoute>
              <EditByQueryRedirect to="/fixed-assets" />
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute>
              <InventoryPage />
            </ProtectedRoute>
          } />
          {/* iter922d - legacy CTA links from action-engine notifications.
              Redirect to the canonical /inventory?tab=month-end so the
              "Enter Closing Stock" CTA on the dashboard never lands on a blank page. */}
          <Route path="/inventory/closing-stock" element={<Navigate to="/inventory?tab=month-end" replace />} />
          <Route path="/inventory/month-end" element={<Navigate to="/inventory?tab=month-end" replace />} />
          {/* iter922d - /settings/payments was a broken action-engine target. The
              canonical Settings payments tab is /settings?tab=payments. */}
          <Route path="/settings/payments" element={<Navigate to="/settings?tab=payments" replace />} />
          {/* iter922d - /reconciliation/:accountId was a broken action-engine target.
              Bank reconciliation lives inside Bank Transactions Center. */}
          <Route path="/reconciliation/:accountId" element={<ReconciliationDeepLinkRedirect />} />
          {/* Billing: iOS shows a READ-ONLY plan & usage view (see
              BillingDashboardPage iosReadOnly branch) — Apple compliance
              is handled inside the page (mutation buttons hidden, plan
              & card management redirect to web).
              Feb 2026 · The old IOSAccountPage stub is only kept for
              /pricing, which stays a fee-free web-managed screen. */}
          <Route path="/pricing" element={isIOSNative() ? <IOSAccountPage /> : <PricingPage />} />
          <Route path="/billing" element={
            <ProtectedRoute>
              <BillingDashboardPage />
            </ProtectedRoute>
          } />
          {/* Emails + older AASA claims sometimes deep-link to
              /settings/billing which was never registered — redirect it
              to the canonical /billing to prevent a 404 both in the
              browser and via Universal Links. */}
          <Route path="/settings/billing" element={<Navigate to="/billing" replace />} />
          {/* Sprint 5 - Learning Centre */}
          <Route path="/learn" element={
            <ProtectedRoute>
              <LearnPage />
            </ProtectedRoute>
          } />
          {/* Phase E - Activity & Recovery Center */}
          <Route path="/activity-recovery" element={
            <ProtectedRoute>
              <ActivityRecoveryPage />
            </ProtectedRoute>
          } />
          <Route path="/statement-upload" element={<Navigate to="/bank-feeds" replace />} />
          <Route path="/akountfly-ai" element={
            <ProtectedRoute>
              <AkountFlyAIPage />
            </ProtectedRoute>
          } />
          {/* iter971k-T · Help Center - public, no auth required. */}
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/help/category/:slug" element={<HelpCategoryPage />} />
          <Route path="/help/:slug" element={<HelpArticlePage />} />
          <Route path="/tax-compliance" element={
            <ProtectedRoute>
              <LegalFormRequired><TaxCompliancePage /></LegalFormRequired>
            </ProtectedRoute>
          } />
          <Route path="/annual-tax-computation" element={
            <ProtectedRoute>
              <LegalFormRequired><AnnualTaxComputationPage /></LegalFormRequired>
            </ProtectedRoute>
          } />
          <Route path="/mileage" element={
            <ProtectedRoute><DashboardLayout><MileagePage /></DashboardLayout></ProtectedRoute>
          } />
          <Route path="/tax-pack-admin" element={
            <ProtectedRoute>
              <TaxPackAdminPage />
            </ProtectedRoute>
          } />
          <Route path="/tax-remittance" element={
            <ProtectedRoute>
              <LegalFormRequired><TaxRemittancePage /></LegalFormRequired>
            </ProtectedRoute>
          } />
          {/* iter1428 · Sub-ledger audit page for the 3 new
              collections written by bank-feed linkage cascades:
              wht_payables, asset_disposals, payroll_remittances. */}
          <Route path="/reports/sub-ledgers" element={
            <ProtectedRoute>
              <SettlementSubLedgersPage />
            </ProtectedRoute>
          } />
          <Route path="/payroll" element={
            <ProtectedRoute>
              <PayrollCountryGate><PayrollHomePage /></PayrollCountryGate>
            </ProtectedRoute>
          } />
          <Route path="/payroll/settings" element={
            <ProtectedRoute>
              <PayrollCountryGate><EmployerPayrollSettingsPage /></PayrollCountryGate>
            </ProtectedRoute>
          } />
          <Route path="/payroll/people" element={
            <ProtectedRoute>
              <PayrollCountryGate><PayrollPeoplePage /></PayrollCountryGate>
            </ProtectedRoute>
          } />
          <Route path="/payroll/runs" element={
            <ProtectedRoute>
              <PayrollCountryGate><PayrollRunsPage /></PayrollCountryGate>
            </ProtectedRoute>
          } />
          <Route path="/payroll/runs/:runId" element={
            <ProtectedRoute>
              <PayrollCountryGate><PayrollRunsPage /></PayrollCountryGate>
            </ProtectedRoute>
          } />
          <Route path="/tax-setup" element={
            <ProtectedRoute>
              <DashboardLayout><TaxSetupPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/payroll/setup" element={
            <ProtectedRoute>
              <PayrollCountryGate><PayrollSetupPage /></PayrollCountryGate>
            </ProtectedRoute>
          } />
          <Route path="/payroll/year-end" element={
            <ProtectedRoute>
              <PayrollCountryGate><PayrollYearEndPage /></PayrollCountryGate>
            </ProtectedRoute>
          } />
          <Route path="/payroll/notices" element={
            <ProtectedRoute>
              <PayrollCountryGate><PayrollNoticesPage /></PayrollCountryGate>
            </ProtectedRoute>
          } />
          {/* iter859: Sprint 7 - Employee Self-Serve Portal. PUBLIC route (no ProtectedRoute);
              auth is handled inside the page via magic-link → portal JWT. */}
          <Route path="/portal/employee" element={<EmployeePortalPage />} />
          <Route path="/partner" element={<PartnerPortalPage />} />
          <Route path="/partners/apply" element={<PartnerApplyPage />} />
          <Route path="/partners" element={<PartnerProgramPage />} />
          <Route path="/partners/policy" element={<PartnerPolicyPage />} />
          <Route path="/r/:code" element={<ReferralLandingPage />} />
          <Route path="/payroll/reports" element={
            <ProtectedRoute>
              <PayrollCountryGate><PayrollReportsPage /></PayrollCountryGate>
            </ProtectedRoute>
          } />
          <Route path="/data-migration" element={
            <ProtectedRoute>
              <DataMigrationPage />
            </ProtectedRoute>
          } />
          <Route path="/opening-balance-ai" element={
            <ProtectedRoute>
              <OpeningBalanceAIWizardPage />
            </ProtectedRoute>
          } />
          <Route path="/data-export" element={
            <ProtectedRoute>
              <DataExportPage />
            </ProtectedRoute>
          } />
          <Route path="/loans" element={
            <ProtectedRoute>
              <LoanSchedulePageV2 />
            </ProtectedRoute>
          } />
          <Route path="/loans/:id/edit" element={
            <ProtectedRoute>
              <EditByQueryRedirect to="/loans" />
            </ProtectedRoute>
          } />
          <Route path="/loan-schedule" element={<Navigate to="/loans" replace />} />
          <Route path="/accounting/loans" element={<Navigate to="/loans" replace />} />
          <Route path="/loans-old" element={
            <ProtectedRoute>
              <LoanSchedulePage />
            </ProtectedRoute>
          } />
          <Route path="/investments" element={
            <ProtectedRoute>
              <InvestmentPageV2 />
            </ProtectedRoute>
          } />
          <Route path="/special-items/tracking" element={
            <ProtectedRoute>
              <UntrackedSpecialItemsPage />
            </ProtectedRoute>
          } />
          <Route path="/investments/:id/edit" element={
            <ProtectedRoute>
              <EditByQueryRedirect to="/investments" />
            </ProtectedRoute>
          } />
          <Route path="/investments-old" element={
            <ProtectedRoute>
              <InvestmentPage />
            </ProtectedRoute>
          } />
          <Route path="/fx-rates" element={
            <ProtectedRoute>
              <FXRatesPage />
            </ProtectedRoute>
          } />
          <Route path="/budget" element={
            <ProtectedRoute>
              <BudgetPage />
            </ProtectedRoute>
          } />
          <Route path="/budget/create" element={<Navigate to="/budget/v2/create" replace />} />
          <Route path="/budget/v2/create" element={
            <ProtectedRoute>
              <BudgetCreateV2Page />
            </ProtectedRoute>
          } />
          {/* iter 908 - Plaid OAuth redirect resume page (public route - protected by sessionStorage token) */}
          <Route path="/oauth/plaid" element={<PlaidOAuthResumePage />} />
          <Route path="/budgets/v2/:id" element={
            <ProtectedRoute>
              <BudgetStatementsV2Page />
            </ProtectedRoute>
          } />
          <Route path="/budget/vs-actuals" element={
            <ProtectedRoute>
              <BudgetVsActualsPage />
            </ProtectedRoute>
          } />
          <Route path="/budget/v2/:budgetId/vs-actuals-3-statement" element={
            <ProtectedRoute>
              <BudgetVsActuals3StatementPage />
            </ProtectedRoute>
          } />
          <Route path="/period-close" element={
            <ProtectedRoute>
              <PeriodClosePage />
            </ProtectedRoute>
          } />
          <Route path="/period-close/:period" element={
            <ProtectedRoute>
              <PeriodClosePage />
            </ProtectedRoute>
          } />
          {/* /import route now redirects to data migration setup */}
          <Route path="/import" element={<Navigate to="/data-migration" replace />} />
          {/* /opening-balances shortcut route */}
          <Route path="/opening-balances" element={<Navigate to="/data-migration" replace />} />
          {/* Redirect removed pages */}
          <Route path="/tax-governance" element={<Navigate to="/settings?tab=tax" replace />} />
          <Route path="/settings/tax" element={<Navigate to="/settings?tab=tax" replace />} />
          <Route path="/audit-log" element={<Navigate to="/settings?tab=activity-log" replace />} />
          <Route path="/advanced-accounting" element={
            <ProtectedRoute>
              <AdvancedAccountingPage />
            </ProtectedRoute>
          } />
          {/* Legacy "Bank Statement Reconciliation" page removed - reconciliation lives in Bank Feeds → Reconciliation */}
          <Route path="/bank-reconciliation" element={<Navigate to="/bank-feeds?tab=reconciliation" replace />} />
          <Route path="/scheduled-reports" element={
            <ProtectedRoute>
              <DashboardLayout>
                <ScheduledReportsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          {/* Public Pages - No Auth Required */}
          {/* iter919 SEO: canonical URLs are /privacy-policy and /terms-of-service.
              /privacy and /terms 301-redirect to avoid duplicate-content penalties. */}
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          
          {/* REMOVED: /mode-select route - SEPARATE PRODUCTS ARCHITECTURE */}
          {/* Business and Personal are now separate products with separate logins */}
          {/* Redirect old mode-select to appropriate dashboard */}
          <Route path="/mode-select" element={<Navigate to="/dashboard" replace />} />
          
          {/* Personal Finance - under development, redirect all /personal/* to dashboard */}
          <Route path="/personal/*" element={<Navigate to="/dashboard" replace />} />

          {/* ==================== ADMIN DASHBOARD ==================== */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="businesses" element={<AdminBusinessesPage />} />
            <Route path="businesses/:businessId" element={<AdminBusiness360Page />} />
            <Route path="financial-ops" element={<AdminFinancialOpsPage />} />
            <Route path="ai-analytics" element={<AdminAIAnalyticsPage />} />
            <Route path="integrations" element={<AdminIntegrationHealthPage />} />
            {/* Consolidated hubs */}
            <Route path="revenue" element={<AdminRevenueHubPage />} />
            <Route path="comms" element={<AdminCommsHubPage />} />
            <Route path="platform-health" element={<AdminPlatformHealthHubPage />} />
            <Route path="bank-feeds" element={<AdminBankFeedsHubPage />} />
            {/* Legacy URLs → hub redirects */}
            <Route path="billing" element={<Navigate to="/admin/revenue?tab=billing" replace />} />
            <Route path="onboarding-funnel" element={<Navigate to="/admin/revenue?tab=funnel" replace />} />
            <Route path="messaging" element={<Navigate to="/admin/comms?tab=messaging" replace />} />
            <Route path="broadcasts" element={<Navigate to="/admin/comms?tab=broadcasts" replace />} />
            <Route path="system" element={<Navigate to="/admin/platform-health?tab=system" replace />} />
            <Route path="data-quality" element={<Navigate to="/admin/platform-health?tab=data-quality" replace />} />
            <Route path="activity" element={<Navigate to="/admin/platform-health?tab=audit" replace />} />
            <Route path="bank-feed-health" element={<Navigate to="/admin/bank-feeds?tab=health" replace />} />
            <Route path="parser-rules" element={<AdminParserRulesPage />} />
            <Route path="bank-feed-classifier" element={<Navigate to="/admin/bank-feeds?tab=classifier" replace />} />
            <Route path="support" element={<AdminSupportToolsPage />} />
            <Route path="country-requests" element={<AdminCountryRequestsPage />} />
            <Route path="partners" element={<AdminPartnersPage />} />
            <Route path="partners/settings" element={<AdminPartnerSettingsPage />} />
            <Route path="partners/:channelId" element={<AdminPartnerDetailPage />} />
            <Route path="rule-watcher" element={<AdminRuleWatcherPage />} />
            <Route path="recon-integrity" element={<AdminReconIntegrityPage />} />
            <Route path="master-tax" element={<AdminMasterTaxPage />} />
            {/* iter971k-V - KB screenshot uploader */}
            <Route path="kb-screenshots" element={<AdminKnowledgeScreenshotsPage />} />
            {/* iter1349 - Merchant KB admin (curate the 5,600-entry company_kb) */}
            <Route path="merchant-kb" element={<AdminMerchantKBPage />} />
            {/* iter971k-Y - User escalations queue */}
            <Route path="escalations" element={<AdminEscalationsPage />} />
          </Route>

          {/* iter-phase3 F12 - catch-all 404 (was a blank page before) */}
          {LEGACY_ROUTES.map(([from, to, mapQuery]) => (
            <Route key={from} path={from} element={<LegacyRedirect to={to} mapQuery={mapQuery} />} />
          ))}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </RouteSuspense>
        <CookieConsent />
        <InstallAppPrompt />
              </ProactivePromptsProvider>
                </BackgroundJobsProvider>
            </RealtimeProvider>
          </UserModeProvider>
        </AccountingModeProvider>
          </TourProvider>
          </DisplayModeProvider>
        </BillingProvider>
        </StepUpProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
    </AppErrorBoundary>
    <ReportIssueHost />
    </>
  );
}

export default App;
